import { withNamespaces } from 'react-i18next';
import { connect } from 'react-redux';
import { Row, Col, Label, Button, ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle, Alert, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { AvForm, AvField, AvGroup, AvInput, AvFeedback } from "availity-reactstrap-validation";
import Select from "react-select";
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import { toast } from "react-toastify";
import ThirdpartyUsersService from "src/modules/3rd-party-management/apis/common/ThirdpartyUsersService";
import { RiDeleteBin5Line } from "react-icons/ri";
import SupplierService from 'src/modules/3rd-party-management/apis/thirdparty/SupplierService';
import LocalizedPhoneInput from "src/modules/3rd-party-management/components/LocalizedPhoneInput";
import CreateAndEditModal from './components/CreateAndEditModal';
import DateUtils from 'src/services/utils/DateUtils';
import Accordion from 'react-bootstrap/Accordion';
import GeneralInformationImage from "src/modules/3rd-party-management/assets/images/general-information.svg";
import ContactPersonImage from "src/modules/3rd-party-management/assets/images/contact-person.svg";
import ShareholderImage from "src/modules/3rd-party-management/assets/images/shareholders.svg";
import ManagementTeamImage from "src/modules/3rd-party-management/assets/images/management-team.svg";
import LocationImage from "src/modules/3rd-party-management/assets/images/location.svg";
import ManagementTeam from './components/management-team';
import GeneralInfos from './components/GeneralInfos';

const CompanyDetails = ({
    t,
    onSubmitSucceed,
    Organization
}) => {
    const [ officeAddresses, setOfficeAddresses ] = useState([{}]);
    const [ factoryAddresses, setFactoryAddresses ] = useState([{}]);
    const [ shareholders, setShareholders ] = useState({
        individual    :   [],
        company       :   []
    });
    const [ managementTeam, setManagementTeam ] = useState([]);
    const [ responsiblePerson, setResponsiblePerson ] = useState({});
    const [ responsiblePersonTitleDrop, setResponsiblePersonTitleDrop ] = useState(false);
    const [ showShareholderModal, setShowShareholderModal ] = useState(false);
    const [ selectedShareholderToEdit, setSelectedShareholderToEdit ] = useState(null);
    const [ shareholderToDelete, setShareholderToDelete ] = useState(null);
    const [ activeAccordion, setActiveAccordion ] = useState('general-information');

    const dateUtils = new DateUtils();

    const handleFetchCompanyDetailQuery = useQuery({
        queryKey: ['3rd-party-management-fetch-supplier-detail-query'],
        queryFn: async () => {
            const service = SupplierService.getInstance();

            return await service.fetchCompanyDetail();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled:true,
        onError: (error) => {
            toast(t('An error occurred while fetching supplier information.'), {
                type: 'error',
            });
        }
	});

    const handleFetchCountriesListQuery = useQuery({
		queryKey: ['3rd-party-management-fetch-countries-list-query'],
		queryFn: async () => {
			const service = ThirdpartyUsersService.getInstance();

			return await service.fetchCountries();
		},
		cacheTime: 0,
		refetchOnWindowFocus: false,
		onError: (error) => {
			toast(t('An error occurred while fetching countries list.'), {
				type: 'error',
			});
		}
	});

    const handleUpdateCompanyDetailMutation = useMutation({
        mutationFn: async (payload) => {
            const service = SupplierService.getInstance();

            return await service.updateCompanyDetail(payload);
        },
        onSuccess: () => {
            onSubmitSucceed && onSubmitSucceed();
        },
        onError: () => {
            toast(t("An error occurred while updating company details."), {
                type: "error",
            });
        }
    });

    const languages = (Organization?.languages || []).map((lng) => {
        return {
            value       :   lng?.language?.id,
            baseLabel   :   lng.name,
            label       :   t(lng.name),
            code        :   lng.language_code,
        }
    });

    const handleValidSubmit = (e, values) => {
        const locations = ((values.office_addresses || []).map((address) => {
            return {
                ...address,
                type        :   'office',
                locationId  :   address.locationId || undefined
            }
        })).concat((values.factory_addresses || []).map((address) => {
            return {
                ...address,
                type        :   'manufacture',
                locationId  :   address.locationId || undefined
            }
        }));

        handleUpdateCompanyDetailMutation.mutate({
            generalInfo         :   values.company_details,
            responsiblePerson   :   values.responsible_person,
            locations           :   locations,
            individualShareHolders  :   shareholders.individual.map((shareholder) => {
                return {
                    ...shareholder,
                    shareHolderId   :   shareholder.id,
                    id              :   undefined,
                    index           :   undefined
                }
            }),
            companyShareHolders     :   shareholders.company.map((shareholder) => {
                return {
                    ...shareholder,
                    shareHolderId   :   shareholder.id,
                    id              :   undefined,
                    index           :   undefined
                }
            }),
            teamManagers: managementTeam.map((teamItem) => {
                return {
                    ...teamItem,
                    managerId: !teamItem.isNew ? teamItem.id : undefined,
                    id: undefined,
                    isNew: undefined,
                    createdBy: undefined
                }
            })
        });
    }

    const countries = useMemo(() => {
        return (Array.isArray(handleFetchCountriesListQuery.data) ? handleFetchCountriesListQuery.data : []).map((country) => {
            return {
                value: country.id,
                baseLabel: country.name,
                label: t(country.name),
                code: country.code
            };
        });
    }, [
        handleFetchCountriesListQuery.data,
        t
    ]);

    useEffect(() => {
        if(handleFetchCompanyDetailQuery.data){
            const {
                locations,
                teamManagers,
                responsiblePerson   :   responsiblePersonData,
                shareHolders        :   prevShareholders,
            } = handleFetchCompanyDetailQuery.data;

            setOfficeAddresses([
                ...((locations || []).filter((location) => location.type === 'office').map((location, i) => {
                    const country = countries.find((c) => c.value === location.country);

                    return {
                        locationId  :   location.id,
                        country     :   {
                            value   :   location.country,
                            label   :   country?.label,
                            baseLabel:  country?.baseLabel 
                        },
                        postalCode  :   location.postalCode,
                        city        :   location.city,
                        address     :   location.address
                    }
                }))
            ]);

            setFactoryAddresses([
                ...((locations || []).filter((location) => location.type === 'manufacture').map((location, i) => {
                    const country = countries.find((c) => c.value === location.country);

                    return {
                        locationId  :   location.id,
                        country     :   {
                            value   :   location.country,
                            label   :   country?.label,
                            baseLabel: country?.baseLabel
                        },
                        city        :   location.city,
                        postalCode  :   location.postalCode,
                        address     :   location.address
                    }
                }))
            ]);

            const responsiblePersonLng = languages.find((lang) => lang.value === responsiblePersonData.language) || {};

            setResponsiblePerson({
                ...responsiblePersonData,
                language    :   {
                    ...responsiblePersonLng,
                    value   :   responsiblePersonData.language
                }
            });

            setShareholders({
                individual  :   (prevShareholders?.individual || []).map((shareholder) => {
                    return {
                        ...shareholder,
                        residenceCountry    :   shareholder.residenceCountry?.id,
                        type                :   'individual'
                    }
                }),
                company     :   (prevShareholders?.company || []).map((shareholder) => {
                    return {
                        ...shareholder,
                        country     :   shareholder.country?.id,
                        type        :   'company'
                    }
                })
            });

            setManagementTeam((teamManagers || []));
        }
    }, [ handleFetchCompanyDetailQuery.data]);

    useEffect(() => {
        if(!selectedShareholderToEdit){
            setShowShareholderModal(false)
        }
        else{
            setShowShareholderModal(true)
        }
    }, [selectedShareholderToEdit])

    const countriesListIsLoading = handleFetchCountriesListQuery.isFetching || handleFetchCountriesListQuery.isLoading;

    const isFormSubmiting = handleUpdateCompanyDetailMutation.isLoading || handleUpdateCompanyDetailMutation.isFetching;

    const isDetailsLoading = handleFetchCompanyDetailQuery.isFetching || handleFetchCompanyDetailQuery.isLoading;

    return (
        <React.Fragment>
            <CreateAndEditModal
                show={showShareholderModal}
                toggle={() => {
                    setShowShareholderModal(false)
                }}
                onSubmit={(type, values, action) => {
                    if(action === 'create'){
                        setShareholders((currentHolders) => {
                            return {
                                ...currentHolders,
                                [type] : [...currentHolders[type], {...values, type : type}]
                            }
                        })
                    }
                    else{
                        setShareholders((currentHolders) => {
                            const newList = [...currentHolders[type]]
                            newList[values.index] = {
                                ...values,
                                type : type
                            }

                            return {
                                ...currentHolders,
                                [type] : newList
                            }
                        })
                    }

                    setShowShareholderModal(false)
                }}
                countries={countries}
                shareholder={selectedShareholderToEdit}
                onClosed={() => setSelectedShareholderToEdit(null)}
            />

            <Modal isOpen={ !!shareholderToDelete } backdrop="static" size="md">
                <ModalHeader toggle={() => setShareholderToDelete(null)}>
                    {t("Confirmation Form")}
                </ModalHeader>

                <ModalBody>
                    <p>
                        {t('Do you want to delete the shareholder?')}
                    </p>
                </ModalBody>

                <ModalFooter>
                    <Button onClick={() => {
                        setShareholders((currentHolders) => {
                            const newList = [...currentHolders[shareholderToDelete.type]];
                            newList.splice(shareholderToDelete.index, 1);

                            setTimeout(() => {
                                setShareholderToDelete(null)
                            }, 100);

                            return {
                                ...currentHolders,
                                [shareholderToDelete.type] : newList
                            }
                        })
                    }} color="danger" type="button" size="md">
                        { t('Delete') }
                    </Button>

                    <Button onClick={() => setShareholderToDelete(null)} color="secondary" type="button" outline>
                        { t('Cancel') }
                    </Button>
                </ModalFooter>
            </Modal>

            <AvForm onValidSubmit={ handleValidSubmit } className="needs-validation m-2">
                <div className='supplier-details-page'>
                    <Accordion className='custom-accordion' defaultActiveKey='general-information' onSelect={(e) => setActiveAccordion(e)}>
                        <Accordion.Item 
                            eventKey='general-information' 
                            className={activeAccordion === 'general-information' ? 'active' : ''}
                            style={{
                                overflow: 'visible'
                            }}
                        >
                            <Accordion.Header>
                                <img alt='General Information' src={ GeneralInformationImage } />
                                <span>
                                    { t('General Information') }
                                </span>
                            </Accordion.Header>
                            
                            <Accordion.Body>
                                <GeneralInfos 
                                    handleFetchCompanyDetailQuery={handleFetchCompanyDetailQuery}
                                    memoizedCountriesList={countries}
                                    countriesListIsLoading={countriesListIsLoading}
                                />
                            </Accordion.Body>
                        </Accordion.Item>

                        <Accordion.Item 
                            eventKey='locations'
                            className={activeAccordion === 'locations' ? 'active' : ''}
                        >
                            <Accordion.Header>
                                <img src={ LocationImage } alt="Locations" />
                                <span>
                                    { t('Locations') }
                                </span>
                            </Accordion.Header>
                            
                            <Accordion.Body>
                                <div className='mb-4'>
                                    <Label>
                                        { `${t('Office Address')}: ` }
                                    </Label>
                                    {officeAddresses.map((item, i) => {
                                        return (
                                            <Row key={i}>
                                                <AvInput 
                                                    name={`office_addresses[${i}][locationId]`}
                                                    type="hidden"
                                                    value={ item?.locationId }
                                                />

                                                <Col md="3" sm="12" className="mb-3">
                                                    <Select
                                                        isDisabled={ countriesListIsLoading || isDetailsLoading }
                                                        isLoading={ countriesListIsLoading || isDetailsLoading }
                                                        placeholder={t("Country")}
                                                        classNamePrefix="select2-selection"
                                                        options={countries}
                                                        menuPortalTarget={document.body}
                                                        onChange={(e) => {
                                                            setOfficeAddresses((currentAddresses) => {
                                                                const newAddresses = [...currentAddresses];
                                                                newAddresses[i] = { ...newAddresses[i], country: e };
                                                                return newAddresses;
                                                            });
                                                        }}
                                                        value={officeAddresses[i]?.country ? {
                                                            ...officeAddresses[i]?.country,
                                                            label: t(officeAddresses[i].country.baseLabel)
                                                        } : null}
                                                    />

                                                    <AvField
                                                        name={`office_addresses[${i}][country]`}
                                                        type="hidden"
                                                        errorMessage={t("This field cannot be blank")}
                                                        className="form-control"
                                                        validate={{
                                                            required: { value: true },
                                                        }}
                                                        value={officeAddresses[i]?.country?.value}
                                                    />
                                                </Col>

                                                <Col md="2" sm="12" className="mb-3">
                                                    <AvField
                                                        name={`office_addresses[${i}][postalCode]`}
                                                        placeholder={t("Postal Code")}
                                                        type="text"
                                                        errorMessage={t("This field cannot be blank")}
                                                        className="form-control"
                                                        onChange={(e) => {
                                                            officeAddresses[i].postalCode = e.target.value
                                                        }}
                                                        validate={{
                                                            required: { value: true },
                                                        }}
                                                        value={officeAddresses[i]?.postalCode || ''}
                                                        disabled={ isDetailsLoading }
                                                    />
                                                </Col>

                                                <Col md="2" sm="12" className="mb-3">
                                                    <AvField
                                                        name={`office_addresses[${i}][city]`}
                                                        placeholder={t("City")}
                                                        type="text"
                                                        errorMessage={t("This field cannot be blank")}
                                                        className="form-control"
                                                        validate={{
                                                            required: { value: true },
                                                        }}
                                                        onChange={(e) => {
                                                            officeAddresses[i].city = e.target.value
                                                        }}
                                                        value={officeAddresses[i]?.city || ''}
                                                        disabled={ isDetailsLoading }
                                                    />
                                                </Col>

                                                <Col md="4" sm="12" className="mb-3">
                                                    <AvField
                                                        name={`office_addresses[${i}][address]`}
                                                        placeholder={t("Street, Number")}
                                                        type="text"
                                                        errorMessage={t("This field cannot be blank")}
                                                        className="form-control"
                                                        onChange={(e) => {
                                                            officeAddresses[i].address = e.target.value
                                                        }}
                                                        validate={{
                                                            required: { value: true },
                                                        }}
                                                        value={officeAddresses[i]?.address || ''}
                                                        disabled={ isDetailsLoading }
                                                    />
                                                </Col>

                                                <Col md="1" sm="12" className="mb-3" hidden={officeAddresses.length <= 1}>
                                                    <h4 style={{ cursor: "pointer" }} className="p-1 text-danger" >
                                                        <RiDeleteBin5Line onClick={() => {
                                                            setOfficeAddresses([
                                                                ...officeAddresses.filter((_, ix) => ix !== i)
                                                            ]);
                                                        }} />
                                                    </h4>
                                                </Col>
                                            </Row>
                                        );
                                    })}

                                    <Row>
                                        <Col sm="12" style={{ display: "flex", justifyContent: "flex-end" }}>
                                            <Button color="primary"
                                                onClick={() => {
                                                    setOfficeAddresses([
                                                        ...officeAddresses,
                                                        {}
                                                    ])
                                                }}
                                                outline>
                                                {" + " + t("Add Location")}
                                            </Button>
                                        </Col>
                                    </Row>
                                </div>

                                <div className='mb-4'>
                                    <Label>
                                        { `${t('Location')}: ` }
                                    </Label>
                                    
                                    {factoryAddresses.map((item, i) => {
                                        return (
                                            <Row key={i}>
                                                <AvInput 
                                                    name={`factory_addresses[${i}][locationId]`}
                                                    type="hidden"
                                                    value={ item?.locationId }
                                                />

                                                <Col md="3" sm="12" className="mb-3">
                                                    <Select placeholder={t("Country")}
                                                        isDisabled={ countriesListIsLoading || isDetailsLoading }
                                                        isLoading={ countriesListIsLoading || isDetailsLoading }
                                                        classNamePrefix="select2-selection"
                                                        id={`factoryCountry-${i}`}
                                                        options={countries}
                                                        menuPortalTarget={document.body}
                                                        onChange={(e) => {
                                                            setFactoryAddresses((currentAddresses) => {
                                                                const newAddresses = [...currentAddresses];
                                                                newAddresses[i] = { ...newAddresses[i], country: e };
                                                                return newAddresses;
                                                            });
                                                        }}
                                                        value={factoryAddresses[i]?.country ? {
                                                            ...factoryAddresses[i]?.country,
                                                            label: t(factoryAddresses[i].country.baseLabel)
                                                        } : null}
                                                    />
                                                    <AvField name={`factory_addresses[${i}][country]`}
                                                        type="hidden"
                                                        errorMessage={t("This field cannot be blank")}
                                                        className="form-control"
                                                        validate={{
                                                            required: { value: true },
                                                        }}
                                                        value={factoryAddresses[i]?.country?.value || null}
                                                    />
                                                </Col>

                                                <Col md="2" sm="12" className="mb-3">
                                                    <AvField
                                                        name={`factory_addresses[${i}][postalCode]`}
                                                        placeholder={t("Postal Code")}
                                                        type="text"
                                                        errorMessage={t("This field cannot be blank")}
                                                        className="form-control"
                                                        onChange={(e) => {
                                                            factoryAddresses[i].postalCode = e.target.value
                                                        }}
                                                        validate={{
                                                            required: { value: true },
                                                        }}
                                                        value={factoryAddresses[i]?.postalCode || ''}
                                                        disabled={ isDetailsLoading }
                                                    />
                                                </Col>

                                                <Col md="2" sm="12" className="mb-3">
                                                    <AvField name={`factory_addresses[${i}][city]`}
                                                        placeholder={t("City")}
                                                        type="text"
                                                        errorMessage={t("This field cannot be blank")}
                                                        className="form-control"
                                                        validate={{
                                                            required: { value: true },
                                                        }}
                                                        onChange={(e) => {
                                                            factoryAddresses[i].city = e.target.value;
                                                        }}
                                                        value={factoryAddresses[i]?.city || ''}
                                                        disabled={ isDetailsLoading }
                                                    />
                                                </Col>

                                                <Col md="4" sm="12" className="mb-3">
                                                    <AvField name={`factory_addresses[${i}][address]`}
                                                        placeholder={t("Street, Number")}
                                                        type="text"
                                                        errorMessage={t("This field cannot be blank")}
                                                        className="form-control"
                                                        validate={{
                                                            required: { value: true },
                                                        }}
                                                        onChange={(e) => {
                                                            factoryAddresses[i].address = e.target.value;
                                                        }}
                                                        value={factoryAddresses[i]?.address || ''}
                                                        disabled={ isDetailsLoading }
                                                    />
                                                </Col>

                                                <Col md="1" sm="12" className="mb-3" hidden={factoryAddresses.length <= 1}>
                                                    <h4 style={{ cursor: "pointer" }} className="p-1 text-danger">
                                                        <RiDeleteBin5Line onClick={() => {
                                                            setFactoryAddresses([
                                                                ...factoryAddresses.filter((_, ix) => ix !== i)
                                                            ]);
                                                        }} />
                                                    </h4>
                                                </Col>
                                            </Row>
                                        );
                                    })}

                                    <Row>
                                        <Col sm="12" style={{ display: "flex", justifyContent: "flex-end" }}>
                                            <Button color="primary"
                                                onClick={() => {
                                                    setFactoryAddresses([
                                                        ...factoryAddresses,
                                                        {}
                                                    ])
                                                }}
                                                outline>
                                                {" + " + t("Add Location")}
                                            </Button>
                                        </Col>
                                    </Row>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>

                        <Accordion.Item 
                            eventKey='management-team'
                            className={activeAccordion === 'management-team' ? 'active' : ''}
                        >
                            <Accordion.Header>
                                <img src={ ManagementTeamImage } alt="Management Team" />
                                <span>
                                    { t('Management Team') }
                                </span>
                            </Accordion.Header>
                            
                            <Accordion.Body>
                                <div className='mb-4'>
                                    <ManagementTeam 
                                        people={managementTeam}
                                        onTeamChange={(newList) => {
                                            setManagementTeam(newList)
                                        }}
                                    />
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>

                        <Accordion.Item 
                            eventKey='responsible-person'
                            className={activeAccordion === 'responsible-person' ? 'active' : ''}
                        >
                            <Accordion.Header>
                                <img src={ ContactPersonImage } alt="Responsible Person" />
                                <span>
                                    { t('Responsible Person') }
                                </span>
                            </Accordion.Header>
                            
                            <Accordion.Body>
                                <div className='mb-4'>
                                    <Row>
                                        <Col sm="12">
                                            <Row>
                                                <Col md="4" sm="4" className="mb-3">
                                                    <Label for="responsible-person-firstname">
                                                        {t("First Name")}
                                                    </Label>

                                                    <AvGroup>
                                                        <div className="d-flex flex-wrap position-relative">
                                                            <ButtonDropdown className="position-absolute" 
                                                                isOpen={ responsiblePersonTitleDrop } 
                                                                toggle={() => {
                                                                    setResponsiblePersonTitleDrop(!responsiblePersonTitleDrop)
                                                                }} 
                                                                style={{
                                                                    left: "0px",
                                                                    top: "0px",
                                                                    width: "74px"
                                                                }}>
                                                                    <DropdownToggle outline split style={{ border: 'none', borderRight: '1px solid rgb(206, 212, 218)' }}>
                                                                        <span style={{marginRight: '8px'}}>
                                                                            { t(responsiblePerson.title || 'Mr.') }
                                                                        </span>
                                                                    </DropdownToggle>

                                                                    <DropdownMenu>
                                                                        <DropdownItem onClick={() => { 
                                                                            setResponsiblePerson({
                                                                                ...responsiblePerson,
                                                                                title   :   'Mr.'
                                                                            });
                                                                        }}>
                                                                            { t('Mr.') }
                                                                        </DropdownItem>

                                                                        <DropdownItem onClick={() => { 
                                                                            setResponsiblePerson({
                                                                                ...responsiblePerson,
                                                                                title   :   'Mrs.'
                                                                            });
                                                                        }}>
                                                                            { t('Mrs.') }
                                                                        </DropdownItem>

                                                                        <DropdownItem onClick={() => { 
                                                                            setResponsiblePerson({
                                                                                ...responsiblePerson,
                                                                                title   :   'Ms.'
                                                                            });
                                                                        }}>
                                                                            { t('Ms.') }
                                                                        </DropdownItem>

                                                                        <DropdownItem onClick={() => { 
                                                                            setResponsiblePerson({
                                                                                ...responsiblePerson,
                                                                                title   :   'Mx.'
                                                                            });
                                                                        }}>
                                                                            { t('Mx.') }
                                                                        </DropdownItem>
                                                                    </DropdownMenu>
                                                            </ButtonDropdown>

                                                            <AvInput 
                                                                id={`responsible-person-title`}
                                                                name={`responsible_person[title]`}
                                                                type="hidden"
                                                                value={ responsiblePerson.title || 'Mr.'}
                                                            />
                                                
                                                            <AvInput id={`responsible-person-firstname`}
                                                                required
                                                                name={`responsible_person[name]`}
                                                                type="text"
                                                                className="form-control"
                                                                style={{
                                                                    paddingLeft: '78px',
                                                                    flexBasis : '100%'
                                                                }}
                                                                value={ responsiblePerson?.name }
                                                                disabled={ isDetailsLoading }
                                                            />

                                                            <AvFeedback style={{
                                                                flexBasis : '100%'
                                                            }}>
                                                                {t("This field cannot be blank")}
                                                            </AvFeedback>
                                                        </div>
                                                    </AvGroup>
                                                </Col>

                                                <Col md="4" sm="12" className="mb-3">
                                                    <Label for="responsible-person-lastname">
                                                        {t("Last Name")}
                                                    </Label>
                                                    <AvField
                                                        name={`responsible_person[lastName]`}
                                                        type="text"
                                                        errorMessage={t("This field cannot be blank")}
                                                        className="form-control"
                                                        validate={{
                                                            required: { value: true },
                                                        }}
                                                        id='responsible-person-lastname'
                                                        value={ responsiblePerson?.lastName }
                                                        disabled={ isDetailsLoading }
                                                    />
                                                </Col>

                                                <Col md="4" sm="12" className="mb-3">
                                                    <Label for="responsible-person-position">
                                                        {t("Position")}
                                                    </Label>
                                                    <AvField
                                                        name={`responsible_person[position]`}
                                                        type="text"
                                                        errorMessage={t("This field cannot be blank")}
                                                        className="form-control"
                                                        validate={{
                                                            required: { value: true },
                                                        }}
                                                        id='responsible-person-position'
                                                        value={ responsiblePerson?.position }
                                                        disabled={ isDetailsLoading }
                                                    />
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col md="4" sm="12" className="mb-3">
                                                    <Label for="responsible-person-language">
                                                        {t("Language")}
                                                    </Label>
                                                    <Select
                                                        isDisabled={ isDetailsLoading }
                                                        isLoading={ isDetailsLoading }
                                                        placeholder={t("Select") + "..."}
                                                        classNamePrefix="select2-selection"
                                                        id='responsible-person-language'
                                                        options={ languages }
                                                        menuPortalTarget={document.body}
                                                        onChange={(e) => {
                                                            setResponsiblePerson({
                                                                ...responsiblePerson,
                                                                language    :   e
                                                            });
                                                        }}
                                                        value={ responsiblePerson?.language ? {
                                                            ...responsiblePerson.language,
                                                            label: t(responsiblePerson.language.baseLabel)
                                                        } : null }
                                                    />
                                                    <AvField
                                                        name={`responsible_person[language]`}
                                                        type="hidden"
                                                        errorMessage={t("This field cannot be blank")}
                                                        validate={{
                                                            required: { value: true },
                                                        }}
                                                        value={responsiblePerson?.language?.value}
                                                    />
                                                </Col>

                                                <Col md="4" sm="12" className="mb-3">
                                                    <Label for="responsible-person-email">
                                                        {t("Email")}
                                                    </Label>
                                                    <AvField
                                                        name={`responsible_person[email]`}
                                                        type="email"
                                                        className="form-control"
                                                        validate={{
                                                            required: {
                                                                value: true,
                                                                errorMessage: t("This field cannot be blank"),
                                                            },
                                                            email: { errorMessage: t("Your email is invalid") },
                                                        }}
                                                        id={`responsible-person-email`}
                                                        value={responsiblePerson?.email}
                                                        disabled={ true }
                                                    />
                                                </Col>

                                                <Col md="4" sm="12" className="mb-3">
                                                    <Label for="responsible-person-phone">
                                                        {t("Phone Number")}
                                                    </Label>

                                                    <LocalizedPhoneInput
                                                        inputClass="w-100"
                                                        country={ "us" }
                                                        inputStyle={{
                                                            height  :   '38px'
                                                        }}
                                                        disableInitialCountryGuess={ false }
                                                        disableCountryGuess={ false }
                                                        onChange={ (e) => {
                                                            setResponsiblePerson({
                                                                ...responsiblePerson,
                                                                phoneNumber    :   e
                                                            });
                                                        }}
                                                        value={ responsiblePerson?.phoneNumber }
                                                        disabled={ isDetailsLoading }
                                                    />

                                                    <AvField
                                                        name={`responsible_person[phoneNumber]`}
                                                        type="hidden"
                                                        errorMessage={t("This field cannot be blank")}
                                                        validate={{
                                                            required: { value: true },
                                                        }}
                                                        id={`responsible-person-phone`}
                                                        value={ responsiblePerson?.phoneNumber }
                                                    />
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>

                        <Accordion.Item 
                            eventKey='holders'
                            className={activeAccordion === 'holders' ? 'active' : ''}
                        >
                            <Accordion.Header>
                                <img src={ ShareholderImage } alt="Shareholders" />
                                <span>
                                    { t('Shareholders') }
                                </span>
                            </Accordion.Header>
                            
                            <Accordion.Body>
                                <div className='mb-4'>
                                    <Row>
                                        <Col sm='12'>
                                            <Label>
                                                {t('Individual')}
                                            </Label>
                                            <div className='table-container table-responsive'>
                                                <table className='border-0'>
                                                    <thead>
                                                            <tr>
                                                                <th>#</th>
                                                                <th>{t('Salutation')}</th>
                                                                <th>{t('First Name')}</th>
                                                                <th>{t('Last Name')}</th>
                                                                <th>{t('Name In Local Language')}</th>
                                                                <th>{t('Last Name In Local Language')}</th>
                                                                <th>{t('Birthday')}</th>
                                                                <th>{t('Resident In Country')}</th>
                                                                <th>{t('Percentage')}</th>
                                                                <th>{t('UBO')}</th>
                                                                <th>{t('Action')}</th>
                                                            </tr>
                                                    </thead>
                                                    {countriesListIsLoading || isDetailsLoading ? (
                                                        <tbody>
                                                            <tr>
                                                                <td>
                                                                    <div className="dt-field dt-skeleton"></div>
                                                                </td>
                                                                <td>
                                                                    <div className="dt-field dt-skeleton"></div>
                                                                </td>
                                                                <td>
                                                                    <div className="dt-field dt-skeleton"></div>
                                                                </td>
                                                                <td>
                                                                    <div className="dt-field dt-skeleton"></div>
                                                                </td>
                                                                <td>
                                                                    <div className="dt-field dt-skeleton"></div>
                                                                </td>
                                                                <td>
                                                                    <div className="dt-field dt-skeleton"></div>
                                                                </td>
                                                                <td>
                                                                    <div className="dt-field dt-skeleton"></div>
                                                                </td>
                                                                <td>
                                                                    <div className="dt-field dt-skeleton"></div>
                                                                </td>
                                                                <td>
                                                                    <div className="dt-field dt-skeleton"></div>
                                                                </td>
                                                                <td>
                                                                    <div className="dt-field dt-skeleton"></div>
                                                                </td>
                                                                <td>
                                                                    <div className="dt-field dt-skeleton"></div>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    ) : (
                                                        <tbody>
                                                            {!shareholders.individual.length && (
                                                                <tr>
                                                                    <td colSpan={11}>
                                                                        <Alert className='text-center' color='warning'>
                                                                            { t('This list is empty') }
                                                                        </Alert>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                            {shareholders.individual.map((item, i) => {
                                                                const country = countries.find((c) => c.value === item.residenceCountry)

                                                                return (
                                                                    <tr>
                                                                        <td>{i + 1}</td>
                                                                        <td>{t(item.title)}</td>
                                                                        <td>{item.firstName}</td>
                                                                        <td>{item.lastName}</td>
                                                                        <td>{item.localLanguageFirstName}</td>
                                                                        <td>{item.localLanguageLastName}</td>
                                                                        <td>{item.birthDay && dateUtils.convertDateToDate(item.birthDay)}</td>
                                                                        <td>{country?.label}</td>
                                                                        <td>{item.percentage}%</td>
                                                                        <td>{item.isUbo ? t('Yes') : t('No')}</td>
                                                                        <td>
                                                                            <Button onClick={() => {
                                                                                setSelectedShareholderToEdit({
                                                                                    ...item,
                                                                                    index : i
                                                                                })
                                                                            }} outline color="primary" size="md" className="border-0">
                                                                                <i className="ri-pencil-line font-size-20"></i>
                                                                            </Button>

                                                                            <Button onClick={() => {
                                                                                setShareholderToDelete({
                                                                                    index   :   i,
                                                                                    type    :   'individual'
                                                                                })
                                                                            }} type='button' outline color="danger" size="md" className="border-0">
                                                                                <i className="ri-delete-bin-line font-size-20"></i>
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            })}
                                                        </tbody>
                                                    )}
                                                </table>     
                                            </div>
                                        </Col>
                                        <Col sm='12'>
                                            <Label>
                                                {t('Company')}
                                            </Label>
                                            <div className='table-container table-responsive'>
                                                <table className='border-0'>
                                                    <thead>
                                                            <tr>
                                                                <th>#</th>
                                                                <th>{t('Name')}</th>
                                                                <th>{t('VAT ID')}</th>
                                                                <th>{t('Name In Local Language')}</th>
                                                                <th>{t('Founding Date')}</th>
                                                                <th>{t('Country')}</th>
                                                                <th>{t('Percentage')}</th>
                                                                <th>{t('Action')}</th>
                                                            </tr>
                                                    </thead>
                                                    {countriesListIsLoading || isDetailsLoading ? (
                                                        <tbody>
                                                            <tr>
                                                                <td><div className="dt-field dt-skeleton"></div></td>
                                                                <td><div className="dt-field dt-skeleton"></div></td>
                                                                <td><div className="dt-field dt-skeleton"></div></td>
                                                                <td><div className="dt-field dt-skeleton"></div></td>
                                                                <td><div className="dt-field dt-skeleton"></div></td>
                                                                <td><div className="dt-field dt-skeleton"></div></td>
                                                                <td><div className="dt-field dt-skeleton"></div></td>
                                                                <td><div className="dt-field dt-skeleton"></div></td>
                                                            </tr>
                                                        </tbody>
                                                    ) : (
                                                        <tbody>
                                                            {!shareholders.company.length && (
                                                                <tr>
                                                                    <td colSpan={11}>
                                                                        <Alert className='text-center' color='warning'>
                                                                            { t('This list is empty') }
                                                                        </Alert>
                                                                    </td>
                                                                </tr>
                                                            )}

                                                            {shareholders.company.map((item, i) => {
                                                                const country = countries.find((c) => c.value === item.country)

                                                                return (
                                                                    <tr>
                                                                        <td>{i + 1}</td>
                                                                        <td>{item.companyName}</td>
                                                                        <td>{item.vatNumber}</td>
                                                                        <td>{item.localLanguageCompanyName}</td>
                                                                        <td>{item.foundingDate && dateUtils.convertDateToDate(item.foundingDate)}</td>
                                                                        <td>{country?.label}</td>
                                                                        <td>{item.percentage}%</td>
                                                                        <td>
                                                                            <Button onClick={() => {
                                                                                setSelectedShareholderToEdit({
                                                                                    ...item,
                                                                                    index : i
                                                                                })
                                                                            }} outline color="primary" size="md" className="border-0">
                                                                                <i className="ri-pencil-line font-size-20"></i>
                                                                            </Button>

                                                                            <Button onClick={() => {
                                                                                setShareholderToDelete({
                                                                                    index   :   i,
                                                                                    type    :   'company'
                                                                                })
                                                                            }} outline color="danger" size="md" className="border-0">
                                                                                <i className="ri-delete-bin-line font-size-20"></i>
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            })}
                                                        </tbody>
                                                    )}
                                                </table>     
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm="12" style={{ display: "flex", justifyContent: "flex-end" }}>
                                            <Button type="button" 
                                                color="primary" 
                                                outline
                                                onClick={() => setShowShareholderModal(true)}>
                                                {" + " + t("Add Shareholder")}
                                            </Button>
                                        </Col>
                                    </Row>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </div>

                <div className="d-flex justify-content-end align-items-center mb-4">
                    <Button color="primary" 
                        className="tpdd-btn" 
                        type="submit"
                        disabled={ isFormSubmiting }>
                            {
                                isFormSubmiting && (
                                    <Spinner className="me-2" animation="border" variant="white" size="sm"/>
                                )
                            }
                        {t('Next')}
                    </Button>
                </div>
            </AvForm>
        </React.Fragment>
    )
};

const mapStatetoProps = (state) => {
	const { Organization } = state;

	return {
		Organization
	};
};

export default withNamespaces()(
    connect(mapStatetoProps, null)(
        CompanyDetails
    )
);